<template>
  <BaseCard>
    <PropertyHotelConnect />
    <div
      v-if="accommodationInfo !== null && !lodgingTypesLoading && !hotelChainsLoading"
      class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between"
    >
      <div
        class="tw:mr-4 tw:h-full tw:w-full tw:md:h-[162px] tw:md:max-w-[162px] tw:overflow-hidden tw:rounded-lg tw:mr-0 tw:md:mr-3"
      >
        <img
          v-if="accommodationInfo?.mainImageUrls"
          class="tw:rounded-lg tw:w-auto tw:h-full tw:object-cover tw:m-auto"
          :src="mainImage"
          :alt="accommodationInfo.name"
        />
        <div v-else class="tw:bg-gray-200 tw:h-full tw:w-full tw:flex tw:items-center tw:justify-center">
          <ImageIcon class="tw:size-6" />
        </div>
      </div>
      <div class="tw:flex-col tw:w-full tw:md:w-5/6">
        <div class="tw:w-full tw:flex tw:justify-between tw:items-start">
          <PropertyName
            :star-rating="accommodationInfo.starRating"
            :name="accommodationInfo.name"
            :lodging-type-label="getLodgingTypeLabel"
          />
          <GhostButton
            size="medium"
            class="tw:px-6 tw:py-2 tw:w-full tw:hidden tw:md:flex tw:md:w-auto tw:mb-2"
            @click="startEditing"
            >{{ $t('app_description_edit') }}</GhostButton
          >
        </div>
        <hr class="tw:bg-gray-300" />
        <PropertyCheckinInfo :checkin="checkIn" :checkout="checkOut" />
        <hr class="tw:bg-gray-300" />
        <PropertyContactInfo
          :address="address"
          :phone="accommodationInfo.phone"
          :fax="accommodationInfo.fax"
          :website="accommodationInfo.homepageUrl"
          :email="accommodationInfo.email"
          :propertyId="accommodationInfo.propertyId"
        />
      </div>
      <div class="tw:md:hidden tw:flex">
        <GhostButton size="medium" class="tw:px-6 tw:py-2 tw:w-full tw:md:w-auto" @click="startEditing">{{
          $t('app_description_edit')
        }}</GhostButton>
      </div>

      <PropertyBasicInfoEditModal
        @toggleEditing="closeEditing"
        :is-open="editing"
        :hotel-chains="hotelChainsList"
        :lodging-types="lodgingTypesArray"
      />
    </div>
  </BaseCard>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useLodgingTypes, useHotelChains } from '@/components/propertyDetails/queries'
import { usePropertyDetailsBasicInfo } from '@/components/propertyDetails/propertyBasicInfo/usePropertyDetailsBasicInfo.js'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import PropertyName from '@/components/propertyDetails/propertyBasicInfo/PropertyName.vue'
import PropertyCheckinInfo from '@/components/propertyDetails/propertyBasicInfo/PropertyCheckinInfo.vue'
import PropertyContactInfo from '@/components/propertyDetails/propertyBasicInfo/PropertyContactInfo.vue'
import PropertyBasicInfoEditModal from '@/components/propertyDetails/propertyBasicInfo/edit/PropertyBasicInfoEditModal.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import ImageIcon from '@/components/icons/ImageIcon.svg?component'
import PropertyHotelConnect from '@/components/propertyDetails/propertyBasicInfo/PropertyHotelConnect.vue'

const route = useRoute()
const store = useStore()
const props = defineProps({
  accommodationInfo: Object,
})
const { setEditObject, clearEditObject } = usePropertyDetailsBasicInfo()

const editing = ref(false)
const address = computed(() => {
  const streetAddress = props.accommodationInfo?.streetAddress ?? ''
  const postalCode = props.accommodationInfo?.postalCode ?? ''
  const city = props.accommodationInfo?.city ?? ''
  return `${streetAddress} ${postalCode} ${city}`
})

const mainImage = computed(
  () =>
    `https://imgcy.trivago.com/c_fill,d_dummy.jpeg,e_sharpen:60,f_auto,h_208,q_auto,w_231/${props.accommodationInfo?.mainImageUrls?.imagePath}`
)
const checkIn = computed(() =>
  props.accommodationInfo.checkInHour !== null
    ? props.accommodationInfo.checkInHour.toString() + ':00'
    : props.accommodationInfo.checkInHour
)
const checkOut = computed(() =>
  props.accommodationInfo.checkOutHour !== null
    ? props.accommodationInfo.checkOutHour.toString() + ':00'
    : props.accommodationInfo.checkOutHour
)
const languageCode = computed(() => store.state.locale?.language)
const { lodgingTypesLoading, lodgingTypes } = useLodgingTypes(languageCode, [101])
const { hotelChainsLoading, hotelChains } = useHotelChains('en-US', [150])

const lodgingTypesArray = computed(() => {
  const lodgingTypesArr = lodgingTypes.value ?? []

  if (!lodgingTypesArr.length) {
    return
  }

  const mapped = lodgingTypesArr.map(lodgingType => ({
    name: lodgingType.translatedName.value,
    id: lodgingType.nsid.id,
    withTranslation: false,
  }))

  return mapped.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
})

const hotelChainsList = computed(() => {
  const hotelChainsArray = hotelChains.value ?? []

  if (!hotelChainsArray.length) {
    return
  }

  const hotelChainList = hotelChainsArray.map(item => ({
    name: item.translatedName.value,
    value: item.nsid.id,
    withTranslation: false,
  }))

  return hotelChainList.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
})
const getLodgingTypeLabel = computed(() => {
  if (!Array.isArray(lodgingTypesArray.value)) return ''
  const accommodationTypeId = props.accommodationInfo?.accommodationTypeId
  if (accommodationTypeId == null) return ''
  return lodgingTypesArray.value.find(item => item.id === accommodationTypeId)?.name ?? ''
})

const startEditing = () => {
  editing.value = true
  setEditObject(props.accommodationInfo)
}

const closeEditing = () => {
  clearEditObject()
  editing.value = false
}
onMounted(() => {
  if (route.query.open_update) {
    startEditing()
  }
})

watch(
  route,
  newRoute => {
    if (newRoute.query.open_update) {
      startEditing()
    }
  },
  { deep: true }
)
</script>
